import axios from 'axios';
import { authState } from './authState';
import { getWeaversWorldApiUrl, getCsrfToken, logger, getWebSocketUrl } from './utils';
import websocketService from './websocketService';

/**
 * Unified communication service for both HTTP and WebSocket
 */
export class CommunicationService {
  constructor() {
    // Create HTTP client
    this.httpClient = this._createHttpClient();
    
    // Use existing WebSocket manager
    this.wsManager = websocketService;
    
    // Subscribe to auth state changes
    this._unsubscribe = authState.subscribe(state => {
      this._updateAuthState(state);
    });
    
    logger.info('Communication', 'Service initialized');
  }
  
  // Update auth state for both HTTP and WebSocket
  _updateAuthState(state) {
    // Update HTTP client auth headers
    this._updateHttpAuth(state);
    
    // Update WebSocket connection with new auth params
    this._updateWsAuth(state);
  }
  
  // Apply auth to HTTP client
  _updateHttpAuth(state) {
    // Always include profile ID if available, regardless of authentication state
    if (state.profileId) {
      this.httpClient.defaults.headers.common = {
        ...this.httpClient.defaults.headers.common,
        'X-Profile-ID': state.profileId.toString()
      };
      logger.debug('Communication', 'Updated HTTP client auth headers', { 
        profileId: state.profileId 
      });
    } else {
      // Clear profile ID header if not available
      delete this.httpClient.defaults.headers.common['X-Profile-ID'];
    }
  }
  
  // Update WebSocket connection with new auth
  _updateWsAuth(state) {
    if (state.isAuthenticated) {
      const params = {
        userId: state.userId,
        profileId: state.profileId
      };
      this.wsManager.updateAuthParams(params);
      logger.debug('Communication', 'Updated WebSocket auth params', params);
    } else {
      this.wsManager.updateAuthParams(null);
    }
  }
  
  // Create configured HTTP client with interceptors
  _createHttpClient() {
    const client = axios.create({
      baseURL: getWeaversWorldApiUrl(),
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    // Add request interceptors for auth, CSRF, etc.
    client.interceptors.request.use(config => {
      // Add CSRF token for mutations
      if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
        const token = getCsrfToken();
        if (token) {
          config.headers['X-CSRFToken'] = token;
        }
      }
      return config;
    });
    
    // Add response interceptors for data unwrapping, error handling
    client.interceptors.response.use(
      response => {
        // Handle standard data format unwrapping
        if (response.data && response.data.data !== undefined) {
          const originalData = response.data;
          response.data = originalData.data;
          
          // DEBUG logging
          logger.debug('API', 'Unwrapped standardized data format', {
            endpoint: response.config.url,
            hasData: true
          });
        }
        return response;
      },
      error => {
        if (error.response) {
          // Handle auth errors (401/403)
          if (error.response.status === 401 || error.response.status === 403) {
            logger.error('API', 'Authentication error', {
              status: error.response.status,
              url: error.config.url
            });
            
            // Unwrap standardized error format if present
            if (error.response.data && error.response.data.data !== undefined) {
              error.response.data = error.response.data.data;
            }
          }
        }
        return Promise.reject(error);
      }
    );
    
    return client;
  }
  
  // Factory method to get a fresh HTTP client instance
  getHttpClient() {
    return this.httpClient;
  }
  
  // Get the WebSocket manager
  getWsManager() {
    return this.wsManager;
  }
  
  // Clean up resources
  dispose() {
    if (this._unsubscribe) {
      this._unsubscribe();
    }
    
    // Disconnect WebSocket
    if (this.wsManager) {
      this.wsManager.disconnect();
    }
  }
}

// Export singleton instance
export const communicationService = new CommunicationService();